import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    Autorenew,
    Refresh,
    Restore,
    Visibility,
    WarningAmber,
} from '@mui/icons-material';
import Layout from './Layout';
import {
    forceReleasePrintBatch,
    getPrintBatchAudit,
    getPrintBatchDetail,
    reconcilePrintBatch,
    releasePrintBatch,
} from '../services/api';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useSnackbar } from '../contexts/SnackbarContext';
import { AuthContext } from '../contexts/AuthContext';

const ACTIVE_STATUSES = new Set(['Sending', 'Releasing', 'Force releasing', 'Reconciling']);
const RECOVERABLE_STATUSES = new Set(['Failed', 'Error', 'Needs attention']);
const STALE_RECOVERY_STATUSES = new Set(['Sending', 'Releasing', 'Force releasing', 'Reconciling']);
const isStaleOperation = (batch) => (
    batch?.updatedAt && Date.now() - new Date(batch.updatedAt).getTime() >= 5 * 60 * 1000
);
const canTakeRecoveryAction = (batch) => (
    RECOVERABLE_STATUSES.has(batch?.status)
    || (STALE_RECOVERY_STATUSES.has(batch?.status) && isStaleOperation(batch))
);
const STATUS_OPTIONS = [
    'Sending',
    'Reconciling',
    'Releasing',
    'Force releasing',
    'Sent',
    'Failed',
    'Error',
    'Needs attention',
    'Released',
    'Force released',
];

const todayDate = () => dayjs().format('YYYY-MM-DD');
const formatDate = (value) => value ? new Date(value).toLocaleString('en-GB') : '—';
const getErrorMessage = (error) => error?.response?.data?.message || error?.message || 'Request failed.';
const statusColor = (status) => {
    if (status === 'Sent') return 'success';
    if (['Failed', 'Error'].includes(status)) return 'error';
    if (['Needs attention', 'Force released'].includes(status)) return 'warning';
    if (status === 'Sending') return 'info';
    return 'default';
};

const PrintBatchAudit = () => {
    const { permissions } = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const [result, setResult] = useState({
        rows: [],
        total: 0,
        page: 1,
        pageSize: 25,
        summary: { sentBatches: 0, sentArtwork: 0, sentCopies: 0 },
    });
    const [filters, setFilters] = useState({
        clientSlug: '',
        status: '',
        startDate: todayDate(),
        endDate: todayDate(),
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [action, setAction] = useState(null);
    const [forceConfirmation, setForceConfirmation] = useState('');

    const canPrint = permissions.includes('PrintProductionJobs');
    const canManage = permissions.includes('ManagePrintWorkflowConfig');

    const loadAudit = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const data = await getPrintBatchAudit({
                ...filters,
                page: page + 1,
                pageSize,
            });
            setResult(data);
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize, showSnackbar]);

    useEffect(() => {
        loadAudit();
    }, [loadAudit]);

    const hasActiveRows = useMemo(
        () => result.rows.some((row) => ACTIVE_STATUSES.has(row.status)),
        [result.rows]
    );

    useEffect(() => {
        if (!hasActiveRows) return undefined;
        const timer = window.setInterval(() => loadAudit({ quiet: true }), 5000);
        return () => window.clearInterval(timer);
    }, [hasActiveRows, loadAudit]);

    const selectedBatchId = selectedBatch?.batchId;
    const selectedBatchStatus = selectedBatch?.status;
    useEffect(() => {
        if (!selectedBatchId || !ACTIVE_STATUSES.has(selectedBatchStatus)) return undefined;
        const timer = window.setInterval(async () => {
            try {
                const data = await getPrintBatchDetail(selectedBatchId);
                setSelectedBatch(data.batch);
            } catch {
                // Keep the current detail visible; the main audit refresh reports API errors.
            }
        }, 5000);
        return () => window.clearInterval(timer);
    }, [selectedBatchId, selectedBatchStatus]);

    const openDetail = async (batchId) => {
        setDetailLoading(true);
        try {
            const data = await getPrintBatchDetail(batchId);
            setSelectedBatch(data.batch);
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setDetailLoading(false);
        }
    };

    const refreshDetailAndAudit = async (batchId) => {
        await loadAudit({ quiet: true });
        await openDetail(batchId);
    };

    const runAction = async (type) => {
        if (!selectedBatch) return;
        setAction(type);
        try {
            if (type === 'reconcile') await reconcilePrintBatch(selectedBatch.batchId);
            if (type === 'release') await releasePrintBatch(selectedBatch.batchId);
            if (type === 'force') await forceReleasePrintBatch(selectedBatch.batchId, forceConfirmation);
            showSnackbar(
                type === 'reconcile' ? 'Batch reconciled.' : 'Batch files released back to the source queue.',
                'success'
            );
            setForceConfirmation('');
            await refreshDetailAndAudit(selectedBatch.batchId);
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setAction(null);
        }
    };

    return (
        <Layout>
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={750}>Batch Print Audit</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Track all client batches, DFE queue entries, progress, errors and release actions.
                            </Typography>
                        </Box>
                        <Button variant="outlined" startIcon={<Refresh />} onClick={() => loadAudit()}>
                            Refresh
                        </Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2 }}>
                        <TextField
                            size="small"
                            label="Client slug"
                            value={filters.clientSlug}
                            onChange={(event) => {
                                setPage(0);
                                setFilters((current) => ({ ...current, clientSlug: event.target.value }));
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={filters.status}
                                onChange={(event) => {
                                    setPage(0);
                                    setFilters((current) => ({ ...current, status: event.target.value }));
                                }}
                            >
                                <MenuItem value="">All statuses</MenuItem>
                                {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <DatePicker
                            label="From"
                            format="DD/MM/YYYY"
                            value={filters.startDate ? dayjs(filters.startDate) : null}
                            onChange={(value) => {
                                setPage(0);
                                setFilters((current) => ({
                                    ...current,
                                    startDate: value?.isValid() ? value.format('YYYY-MM-DD') : '',
                                }));
                            }}
                            slotProps={{
                                textField: { size: 'small', sx: { minWidth: 170 } },
                            }}
                        />
                        <DatePicker
                            label="To"
                            format="DD/MM/YYYY"
                            value={filters.endDate ? dayjs(filters.endDate) : null}
                            onChange={(value) => {
                                setPage(0);
                                setFilters((current) => ({
                                    ...current,
                                    endDate: value?.isValid() ? value.format('YYYY-MM-DD') : '',
                                }));
                            }}
                            slotProps={{
                                textField: { size: 'small', sx: { minWidth: 170 } },
                            }}
                        />
                    </Stack>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ mt: 2 }}
                        flexWrap="wrap"
                    >
                        <Chip
                            color="success"
                            variant="outlined"
                            label={`Artwork sent to DFE: ${result.summary?.sentArtwork ?? 0}`}
                        />
                        <Chip
                            color="success"
                            variant="outlined"
                            label={`Copies sent to DFE: ${result.summary?.sentCopies ?? 0}`}
                        />
                        <Chip
                            variant="outlined"
                            label={`Sent batches: ${result.summary?.sentBatches ?? 0}`}
                        />
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {loading && <LinearProgress />}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Client / product row</TableCell>
                                    <TableCell>Press</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell align="right">Artwork</TableCell>
                                    <TableCell align="right">Copies</TableCell>
                                    <TableCell>Operator</TableCell>
                                    <TableCell>QueueEntry</TableCell>
                                    <TableCell align="right">Detail</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.rows.map((row) => (
                                    <TableRow key={row.batchId} hover>
                                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700}>{row.clientName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.rowName}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.destinationName || '—'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={row.status} color={statusColor(row.status)} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 160 }}>
                                            <Typography variant="caption">
                                                {row.phase} · {row.progressCurrent}/{row.progressTotal}
                                            </Typography>
                                            {ACTIVE_STATUSES.has(row.status) && (
                                                <LinearProgress
                                                    variant={row.progressTotal ? 'determinate' : 'indeterminate'}
                                                    value={row.progressTotal
                                                        ? Math.min(100, (row.progressCurrent / row.progressTotal) * 100)
                                                        : 0}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{row.totalFiles}</TableCell>
                                        <TableCell align="right">{row.totalCopies}</TableCell>
                                        <TableCell>{row.createdByUsername || '—'}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                                                {row.queueEntryId || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                startIcon={detailLoading ? <CircularProgress size={14} /> : <Visibility />}
                                                onClick={() => openDetail(row.batchId)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!loading && !result.rows.length && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                            No print batch history matches these filters.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={result.total}
                        page={page}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(event) => {
                            setPageSize(Number(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            </Stack>

            <Dialog
                open={Boolean(selectedBatch)}
                onClose={() => {
                    setSelectedBatch(null);
                    setForceConfirmation('');
                }}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Batch {selectedBatch?.batchId}</DialogTitle>
                <DialogContent>
                    {selectedBatch && (
                        <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Chip label={selectedBatch.status} color={statusColor(selectedBatch.status)} />
                                <Chip variant="outlined" label={`${selectedBatch.phase} ${selectedBatch.progressCurrent}/${selectedBatch.progressTotal}`} />
                                {selectedBatch.destinationName && (
                                    <Chip variant="outlined" label={selectedBatch.destinationName} />
                                )}
                                {selectedBatch.queueEntryId && (
                                    <Chip variant="outlined" label={`QueueEntry ${selectedBatch.queueEntryId}`} />
                                )}
                            </Stack>
                            {selectedBatch.errorMessage && <Alert severity="error">{selectedBatch.errorMessage}</Alert>}
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Artwork</TableCell>
                                            <TableCell>Relative folder</TableCell>
                                            <TableCell align="right">Copies</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedBatch.files.map((file) => (
                                            <TableRow key={file.fileId}>
                                                <TableCell>{file.fileName}</TableCell>
                                                <TableCell>{file.relativePath}</TableCell>
                                                <TableCell align="right">{file.quantity}</TableCell>
                                                <TableCell><Chip size="small" label={file.status} /></TableCell>
                                                <TableCell>{file.errorMessage || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {canManage && canTakeRecoveryAction(selectedBatch) && (
                                <Alert severity="warning" icon={<WarningAmber />}>
                                    Force Release All can duplicate a job already accepted by Production Pro. Check the QueueEntry first, then type <strong>FORCE RELEASE</strong>.
                                    <TextField
                                        fullWidth
                                        size="small"
                                        sx={{ mt: 1 }}
                                        value={forceConfirmation}
                                        onChange={(event) => setForceConfirmation(event.target.value)}
                                        placeholder="FORCE RELEASE"
                                    />
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap' }}>
                    <Button onClick={() => setSelectedBatch(null)}>Close</Button>
                    {canPrint && selectedBatch?.queueEntryId && canTakeRecoveryAction(selectedBatch) && (
                        <Button
                            startIcon={<Autorenew />}
                            disabled={Boolean(action)}
                            onClick={() => runAction('reconcile')}
                        >
                            Reconcile
                        </Button>
                    )}
                    {canPrint && canTakeRecoveryAction(selectedBatch) && (
                        <Button
                            color="warning"
                            startIcon={<Restore />}
                            disabled={Boolean(action)}
                            onClick={() => runAction('release')}
                        >
                            Safe release
                        </Button>
                    )}
                    {canManage && canTakeRecoveryAction(selectedBatch) && (
                        <Button
                            color="error"
                            variant="contained"
                            startIcon={<WarningAmber />}
                            disabled={Boolean(action) || forceConfirmation !== 'FORCE RELEASE'}
                            onClick={() => runAction('force')}
                        >
                            Force Release All
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default PrintBatchAudit;
